import { YouTubeAnalyticsResponse } from '../interfaces/Analytics';
import { TimeInterval } from '../types/types';
import { AnalyticsData } from '../hooks/useAnalytics';
import api from '../config/axiosConfig';
import {
  CampaignCreatorsReach,
  CampaignDashboardKpis,
  CampaignTopPosts,
} from '../interfaces/campaignKpis';

export const fetchDashboardAnalytics = async (
  timeInterval: string,
): Promise<AnalyticsData> => {
  try {
    const response = await api.get(
      `/analytics/dashboard?interval=${timeInterval}`,
    );
    return response.data as AnalyticsData;
  } catch (error) {
    console.error('Error fetching dashboard analytics:', error);
    throw error;
  }
};

export const fetchAnalytics = async (
  timeInterval: string,
): Promise<AnalyticsData> => {
  try {
    const response = await api.get(`/analytics/full?interval=${timeInterval}`);
    return response.data as AnalyticsData;
  } catch (error) {
    console.error('Error fetching full analytics:', error);
    throw error;
  }
};

export const fetchLinkAnalytics = async (
  linkId: string,
  timeInterval: string,
): Promise<AnalyticsData> => {
  try {
    const response = await api.get(
      `/analytics/link/${linkId}?interval=${timeInterval}`,
    );
    return response.data as AnalyticsData;
  } catch (error) {
    console.error(`Error fetching analytics for link ${linkId}:`, error);
    throw error;
  }
};

export const exportAnalyticsCSV = async (
  timeInterval: string,
): Promise<string> => {
  try {
    const response = await api.get(
      `/analytics/export?interval=${timeInterval}`,
      {
        responseType: 'blob',
      },
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    return url;
  } catch (error) {
    console.error('Error exporting analytics to CSV:', error);
    throw error;
  }
};

export const getAnalyticsData = async (
  interval: TimeInterval | string,
  updateCallback?: (data: any) => void,
): Promise<AnalyticsData> => {
  try {
    // Appel direct sans cache
    return await getAnalyticsDataWithoutCache(interval);
  } catch (error) {
    console.error('Error fetching analytics data:', error);
    throw error;
  }
};

export const getAnalyticsDataWithoutCache = async (
  interval: TimeInterval | string,
): Promise<AnalyticsData> => {
  try {
    let apiInterval = interval;

    if (interval === 'daily') apiInterval = 'day';
    else if (interval === 'weekly') apiInterval = 'week';
    else if (interval === 'monthly') apiInterval = 'month';
    else if (interval === 'yearly') apiInterval = 'year';

    const response = await api.get(`/analytics`, {
      params: {
        timeFrame: apiInterval,
      },
    });

    return response.data as AnalyticsData;
  } catch (error) {
    console.error('Error fetching analytics data without cache:', error);
    throw error;
  }
};

export const getAnalyticsDataByLink = async (
  timeFrame: string,
  linkId: string,
  userId: string,
  onUpdate?: (newData: any) => void,
) => {
  try {
    // Appel direct sans cache
    const response = await api.get(
      `/users/${userId}/links/${linkId}/analytics`,
      {
        params: { interval: timeFrame },
      },
    );
    return response.data;
  } catch (error) {
    console.error('Error when fetching link analytics', error);
    return null;
  }
};

export const getLinkAnalytics = async (
  linkId: string,
  interval: TimeInterval = 'week',
  onUpdate?: (newData: any) => void,
): Promise<any | null> => {
  try {
    const response = await api.get(`/links/${linkId}/analytics`, {
      params: { interval },
    });
    return response.data;
  } catch (error) {
    console.error('Error when fetching link analytics', error);
    return null;
  }
};

export const getUserAnalytics = async (
  userId: string,
  interval: TimeInterval = 'week',
  onUpdate?: (newData: any) => void,
): Promise<any | null> => {
  try {
    // Appel direct sans cache
    const response = await api.get(`/users/${userId}/analytics`, {
      params: { interval },
    });
    return response.data;
  } catch (error) {
    console.error('Error when fetching user analytics', error);
    return null;
  }
};

export const getYouTubeAnalytics = async (
  linkId: string,
  interval: TimeInterval = 'week',
  onUpdate?: (newData: YouTubeAnalyticsResponse) => void,
): Promise<YouTubeAnalyticsResponse | null> => {
  try {
    // Appel direct sans cache
    const response = await api.get<YouTubeAnalyticsResponse>(
      `/links/${linkId}/youtube-analytics`,
      { params: { interval } },
    );
    return response.data;
  } catch (error) {
    console.error('Error when fetching YouTube analytics', error);
    return null;
  }
};

export const fetchTopLinks = async (filter?: TimeInterval) => {
  try {
    const response = await api.get(
      `/analytics/admin/top-links${filter ? `?filter=${filter}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching top links:', error);
    return [];
  }
};

export const fetchTopUsers = async (filter?: TimeInterval) => {
  try {
    const response = await api.get(
      `/analytics/admin/top-users${filter ? `?filter=${filter}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching top users:', error);
    return [];
  }
};

export const fetchTopRegions = async (filter?: TimeInterval) => {
  try {
    const response = await api.get(
      `/analytics/admin/top-regions${filter ? `?filter=${filter}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching top regions:', error);
    return [];
  }
};

export const fetchTopCountries = async (filter?: TimeInterval) => {
  try {
    const response = await api.get(
      `/analytics/admin/top-countries${filter ? `?filter=${filter}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching top countries:', error);
    return [];
  }
};

export const fetchTopDevices = async (filter?: TimeInterval) => {
  try {
    const response = await api.get(
      `/analytics/admin/top-devices${filter ? `?filter=${filter}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching top devices:', error);
    return [];
  }
};

export const fetchTopPages = async () => {
  try {
    const response = await api.get('/analytics/admin/top-pages');
    return response.data;
  } catch (error) {
    console.error('Error fetching top pages:', error);
    return [];
  }
};

export const fetchTopReferrers = async () => {
  try {
    const response = await api.get('/analytics/admin/top-referrers');
    return response.data;
  } catch (error) {
    console.error('Error fetching top referrers:', error);
    return [];
  }
};

export const fetchTopBrowsers = async () => {
  try {
    const response = await api.get('/analytics/admin/top-browsers');
    return response.data;
  } catch (error) {
    console.error('Error fetching top browsers:', error);
    return [];
  }
};

export const fetchTopPlatforms = async (filter?: TimeInterval) => {
  try {
    const response = await api.get(
      `/analytics/admin/top-platforms${filter ? `?filter=${filter}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching top platforms:', error);
    return [];
  }
};

export const fetchTotalClicks = async (filter?: TimeInterval) => {
  try {
    const response = await api.get(
      `/analytics/admin/total-clicks${filter ? `?filter=${filter}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching total clicks:', error);
    return 0;
  }
};

export const fetchNewClicks = async (filter?: TimeInterval) => {
  try {
    const response = await api.get(
      `/analytics/admin/new-clicks${filter ? `?filter=${filter}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching new clicks:', error);
    return 0;
  }
};

export const fetchTotalUsers = async (filter?: TimeInterval) => {
  try {
    const response = await api.get(
      `/analytics/admin/total-users${filter ? `?filter=${filter}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching total users:', error);
    return 0;
  }
};

export const fetchNewUsers = async (filter?: TimeInterval) => {
  try {
    const response = await api.get(
      `/analytics/admin/new-users${filter ? `?filter=${filter}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching new users:', error);
    return 0;
  }
};

export const fetchTotalLinks = async (filter?: TimeInterval) => {
  try {
    const response = await api.get(
      `/analytics/admin/total-links${filter ? `?filter=${filter}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching total links:', error);
    return 0;
  }
};

export const fetchNewLinks = async (filter?: TimeInterval) => {
  try {
    const response = await api.get(
      `/analytics/admin/new-links${filter ? `?filter=${filter}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching new links:', error);
    return 0;
  }
};

export const fetchTotalAnnualSubscriptions = async (filter?: TimeInterval) => {
  try {
    const response = await api.get(
      `/analytics/admin/total-annual-subscriptions${filter ? `?filter=${filter}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching total annual subscriptions:', error);
    return 0;
  }
};

export const fetchTotalMonthlySubscriptions = async (filter?: TimeInterval) => {
  try {
    const response = await api.get(
      `/analytics/admin/total-monthly-subscriptions${filter ? `?filter=${filter}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching total monthly subscriptions:', error);
    return 0;
  }
};

export const fetchUserStatistics = async (filter?: TimeInterval) => {
  try {
    const response = await api.get(
      `/analytics/admin/user-statistics${filter ? `?filter=${filter}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching user statistics:', error);
    return {};
  }
};

export const fetchPerformanceGraphs = async (filter?: TimeInterval) => {
  try {
    const response = await api.get(
      `/analytics/admin/performance-graphs${filter ? `?filter=${filter}` : ''}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching performance graphs:', error);
    return {};
  }
};

/**
 * Récupère les données d'évolution des utilisateurs au fil du temps
 * @param filter Filtre temporel (jour, semaine, mois, année)
 * @returns Tableau de données d'évolution des utilisateurs
 */
export const fetchUsersEvolution = async (filter: string) => {
  try {
    const response = await api.get(
      `/analytics/admin/users-evolution?filter=${filter}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching users evolution', error);
    throw error;
  }
};

/**
 * Récupère les données d'évolution des liens au fil du temps
 * @param filter Filtre temporel (jour, semaine, mois, année)
 * @returns Tableau de données d'évolution des liens
 */
export const fetchLinksEvolution = async (filter: string) => {
  try {
    const response = await api.get(
      `/analytics/admin/links-evolution?filter=${filter}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching links evolution', error);
    throw error;
  }
};

/**
 * Récupère les données de création d'utilisateurs au fil du temps (sans remplissage des jours vides)
 * @param filter Filtre temporel (jour, semaine, mois, année)
 * @returns Objet contenant un tableau de données de création d'utilisateurs
 */
export const fetchUserCreationOverTime = async (filter: string) => {
  try {
    const response = await api.get(
      `/analytics/admin/user-creation-over-time?filter=${filter}`,
    );
    return response.data.userCreationOverTime;
  } catch (error) {
    console.error('Error fetching user creation over time', error);
    throw error;
  }
};

/**
 * Récupère les données de performance des liens au fil du temps
 * @param filter Filtre temporel (jour, semaine, mois, année)
 * @returns Objet contenant un tableau de données de performance des liens
 */
export const fetchLinkPerformanceOverTime = async (filter: string) => {
  try {
    const response = await api.get(
      `/analytics/admin/link-performance-over-time?filter=${filter}`,
    );
    return response.data.linkPerformanceOverTime;
  } catch (error) {
    console.error('Error fetching link performance over time', error);
    throw error;
  }
};

const analyticsRangeQuery = (
  filter: string,
  startDate?: string,
  endDate?: string,
): string => {
  if (filter === 'custom' && startDate && endDate) {
    return `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
  }
  return `?filter=${encodeURIComponent(filter)}`;
};

export const campaignAnalysis = async (
  brandId: string,
  filter = 'all-time',
  startDate?: string,
  endDate?: string,
) => {
  try {
    const response = await api.get(
      `/analytics/dashboard/${brandId}${analyticsRangeQuery(filter, startDate, endDate)}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign analysis', error);
    throw error;
  }
};
export const totalSellChart = async (brandId: string, filter: string) => {
  try {
    const response = await api.get(
      `/analytics/dashboard/${brandId}/chart-analysis1?filter=${filter}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching link performance over time', error);
    throw error;
  }
};
export const earnedMediaValueChart = async (
  brandId: string,
  filter: string,
  startDate?: string,
  endDate?: string,
) => {
  try {
    const response = await api.get(
      `/analytics/dashboard/${brandId}/chart-analysis2${analyticsRangeQuery(filter, startDate, endDate)}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching link performance over time', error);
    throw error;
  }
};
export const contentAnalysisChart = async (
  brandId: string,
  filter = 'all-time',
  startDate?: string,
  endDate?: string,
) => {
  try {
    const response = await api.get(
      `/analytics/dashboard/${brandId}/content-analysis${analyticsRangeQuery(filter, startDate, endDate)}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching link performance over time', error);
    throw error;
  }
};

export const engagementAnalysisChart = async (
  brandId: string,
  filter = 'all-time',
  startDate?: string,
  endDate?: string,
) => {
  try {
    const response = await api.get(
      `/analytics/dashboard/${brandId}/engagement-analysis${analyticsRangeQuery(filter, startDate, endDate)}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching engagement analysis', error);
    throw error;
  }
};

export const creatorAnalysis = async (
  brandId: string,
  filter: string,
  startDate?: string,
  endDate?: string,
) => {
  try {
    const response = await api.get(
      `/analytics/dashboard/creator-by-reach/${brandId}${analyticsRangeQuery(filter, startDate, endDate)}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching creator analysis', error);
    throw error;
  }
};
export const creatorUserDetails = async (
  brandId: string,
  userId: string,
  campaignId: string,
) => {
  try {
    const response = await api.get(
      `/analytics/dashboard/${brandId}/${userId}/submission-details?campaignId=${campaignId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching creator analysis', error);
    throw error;
  }
};

export const getCreatorAllSubmissions = async (
  brandId: string,
  userId: string,
  platform?: string,
) => {
  try {
    const url = platform
      ? `/analytics/dashboard/${brandId}/${userId}/all-submissions?platform=${platform}`
      : `/analytics/dashboard/${brandId}/${userId}/all-submissions`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching creator all submissions', error);
    throw error;
  }
};

export const getCampaignChart = async (
  campaignId: string,
  filter: string,
  startDate?: string,
  endDate?: string,
) => {
  try {
    const response = await api.get(
      `/analytics/dashboard/${campaignId}/campaignChart${analyticsRangeQuery(filter, startDate, endDate)}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign chart', error);
    throw error;
  }
};
export const roasAnalysisChart = async (
  brandId: string,
  filter: string,
  startDate?: string,
  endDate?: string,
) => {
  try {
    const response = await api.get(
      `/analytics/dashboard/${brandId}/roas-analysis${analyticsRangeQuery(filter, startDate, endDate)}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching ROAS analysis chart', error);
    throw error;
  }
};
export const getCampaignCreatorList = async (campaignId: string) => {
  try {
    const response = await api.get(
      `/analytics/dashboard/${campaignId}/creatorList`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign creator list', error);
    throw error;
  }
};

export const getCampaignRoasAnalysisChart = async (
  campaignId: string,
  filter: string,
  startDate?: string,
  endDate?: string,
) => {
  try {
    const response = await api.get(
      `/analytics/dashboard/${campaignId}/roasChart${analyticsRangeQuery(filter, startDate, endDate)}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching ROAS analysis chart', error);
    throw error;
  }
};

export const getBrandSentimentAnalysis = async (
  brandId: string,
  filter?: string,
  startDate?: string,
  endDate?: string,
) => {
  try {
    const response = await api.post('/analytics/dashboard/sentiment', {
      brandId,
      ...(filter && { filter }),
      ...(filter === 'custom' && startDate && { startDate }),
      ...(filter === 'custom' && endDate && { endDate }),
    });
    return response.data as {
      positiveCount: number;
      negativeCount: number;
      sarcasticCount: number;
      positiveRatio: string;
      negativeRatio: string;
      sarcasticRatio: string;
      topPositiveKeywords: {
        keyword: string;
        meaning: string;
        occurrences: number;
      }[];
      topNegativeKeywords: {
        keyword: string;
        meaning: string;
        occurrences: number;
      }[];
      topSarcasticKeywords: {
        keyword: string;
        meaning: string;
        occurrences: number;
      }[];
      dataFrom: string | null;
    };
  } catch (error) {
    console.error('Error fetching brand sentiment analysis', error);
    throw error;
  }
};

/**
 * Campaign-scoped KPIs for the brand dashboard's live-campaign panel. Slimmer
 * than the campaign chart endpoint — no chart series, EMV or deltas — but the
 * figures come from the same aggregations, so the panel and the campaign detail
 * page always agree.
 */
export const getCampaignKpis = async (
  campaignId: string,
): Promise<CampaignDashboardKpis> => {
  try {
    const response = await api.get(`/dashboard-kpis/campaign/${campaignId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign KPIs', error);
    throw error;
  }
};

/**
 * The campaign's six best-performing posts. Ranked by views and capped
 * server-side, so the panel renders the list as given.
 */
export const getCampaignTopPosts = async (
  campaignId: string,
): Promise<CampaignTopPosts> => {
  try {
    const response = await api.get(
      `/dashboard-kpis/campaign/${campaignId}/top-posts`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign top posts', error);
    throw error;
  }
};

/** Pooled Instagram audience of the campaign's creators. */
export const getCampaignCreatorsReach = async (
  campaignId: string,
): Promise<CampaignCreatorsReach> => {
  try {
    const response = await api.get(
      `/dashboard-kpis/campaign/${campaignId}/creators-reach`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign creators reach', error);
    throw error;
  }
};
